'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { AccountMenu } from '@/platform/auth/AccountMenu';
import { LoginDrawer } from '@/platform/auth/LoginDrawer';
import { useAuth } from '@/platform/auth/useAuth';
import { navShowsAccountMenu } from '@/platform/auth/oauthRedirect';
import { nextLinkPrefetch } from '@/platform/lib/nextPrefetch';
import { SiteAppearanceProvider } from '@/platform/siteappearance/SiteAppearanceProvider';
import '@/platform/siteappearance/site-appearance.css';
import { ThemeSettingsProvider } from '@/platform/theme/ThemeSettings';
import { fetchAdminMe } from '@/platform/admin/api';
import { fetchITSMMe } from './api';
import {
  DEFAULT_ITSM_NAV_TREE,
  ITSM_SIDEBAR_TEST_ID,
  filterNavTree,
  hashKeyFromItsmHref,
  itsmHashNavOnDesk,
  titleForItsmLocation,
  type ItsmMeFlags,
  type ItsmNavLink,
  type ItsmNavNode,
} from './itsmNavCatalog';
import './itsm-shell.css';

function currentHash(): string {
  if (typeof window === 'undefined') return 'home';
  return window.location.hash.replace(/^#\/?/, '').split('?')[0] || 'home';
}

function linkIsActive(href: string, pathname: string, hash: string): boolean {
  if (href.startsWith('/gatekeeper')) {
    return pathname.replace(/\/$/, '').endsWith('/gatekeeper');
  }
  const want = (href.split('#')[1] || 'home').replace(/^\//, '');
  const onDesk = pathname.replace(/\/$/, '').endsWith('/servicedesk');
  return onDesk && want === hash;
}

function NavAnchor({ link, pathname, hash }: { link: ItsmNavLink; pathname: string; hash: string }) {
  const active = linkIsActive(link.href, pathname, hash);
  const className = active ? 'is-active' : undefined;
  const ariaCurrent = active ? ('page' as const) : undefined;
  if (itsmHashNavOnDesk(link.href, pathname)) {
    return (
      <a
        href={link.href}
        className={className}
        aria-current={ariaCurrent}
        onClick={(e) => {
          const key = hashKeyFromItsmHref(link.href);
          if (!key) return;
          e.preventDefault();
          const cur = window.location.hash.replace(/^#\/?/, '') || 'home';
          if (cur === key) {
            window.dispatchEvent(new Event('hashchange'));
            return;
          }
          window.location.hash = key;
        }}
      >
        {link.label}
      </a>
    );
  }
  return (
    <Link
      href={link.href}
      prefetch={nextLinkPrefetch()}
      className={className}
      aria-current={ariaCurrent}
    >
      {link.label}
    </Link>
  );
}

function NavLinks({ nodes, pathname, hash }: { nodes: ItsmNavNode[]; pathname: string; hash: string }) {
  return (
    <ul className="itsm-shell__nav-list">
      {nodes.map((node) =>
        node.kind === 'folder' ? (
          <li key={node.label} className="itsm-shell__folder">
            <div className="itsm-shell__folder-label">{node.label}</div>
            <ul className="itsm-shell__folder-children">
              {node.children.map((child) => (
                <li key={child.routeKey}>
                  <NavAnchor link={child} pathname={pathname} hash={hash} />
                </li>
              ))}
            </ul>
          </li>
        ) : (
          <li key={node.routeKey}>
            <NavAnchor link={node} pathname={pathname} hash={hash} />
          </li>
        ),
      )}
    </ul>
  );
}

export function OpsITSMShell({ children }: { children: ReactNode }) {
  const pathname = usePathname() || '/';
  const { user, profile, sessionReady, joinGateError } = useAuth();
  const [hash, setHash] = useState('home');
  const [me, setMe] = useState<ItsmMeFlags>({});

  useEffect(() => {
    const sync = () => setHash(currentHash());
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  useEffect(() => {
    if (!sessionReady || !user) {
      setMe({});
      return;
    }
    void Promise.all([fetchITSMMe(), fetchAdminMe().catch(() => null)])
      .then(([row, admin]) =>
        setMe({
          isAgent: !!(row.isAgent || admin?.isAgent),
          portalAdmin: !!(row.portalAdmin || admin?.portalAdmin || admin?.isAdmin),
        }),
      )
      .catch(() => setMe({}));
  }, [sessionReady, user]);

  const tree = useMemo(() => filterNavTree(DEFAULT_ITSM_NAV_TREE, me), [me]);
  const title = titleForItsmLocation(pathname, hash);
  const showAccount = navShowsAccountMenu({
    sessionReady,
    user,
    profile,
    joinGate: !!joinGateError,
  });

  return (
    <SiteAppearanceProvider>
      <ThemeSettingsProvider>
      <div className="itsm-shell">
        <aside className="itsm-shell__sidebar" data-testid={ITSM_SIDEBAR_TEST_ID}>
          <div className="itsm-shell__brand">
            <span className="itsm-shell__brand-name">Communio</span>
            <span className="itsm-shell__brand-sub">ITSM</span>
          </div>
          <nav className="itsm-shell__nav" aria-label="Communio ITSM">
            <h1 className="itsm-shell__sr">Communio ITSM</h1>
            <NavLinks nodes={tree} pathname={pathname} hash={hash} />
          </nav>
        </aside>
        <div className="itsm-shell__main">
          <header className="itsm-shell__topbar">
            <p className="itsm-shell__topbar-title">{title}</p>
            <div>{showAccount ? <AccountMenu /> : <LoginDrawer />}</div>
          </header>
          <div className="itsm-shell__content">{children}</div>
        </div>
      </div>
      </ThemeSettingsProvider>
    </SiteAppearanceProvider>
  );
}
